import type { IntlShape } from 'react-intl';
import type { CatalogMasterFailReason } from '../../../../../redux/api/fymAPI.types';
import { kitFailText, usesRefundLine } from '../../studio/kitFailMessage';
import { isSoundMismatch, soundCheckOfAsset, sourceOfAsset } from '../../addedAssets/addedAssetFacts';
import { addedAssetsMessages } from '../../addedAssets/addedAssetsMessages';
import { KitSlotView } from './kitSlots';
import { kitMessages } from './kitMessages';

/** The fixed line of a slot that says what it is (design b1 `.ks-s`); null when the slot has none. */
function fixedLine(intl: IntlShape, view: KitSlotView): string | null {
  const { id } = view.slot;
  if (id === 'coverSizes') {
    return intl.formatMessage(kitMessages.coverSizesLine);
  }
  if (id === 'spotifyHeader') {
    return intl.formatMessage(kitMessages.spotifyHeaderLine);
  }
  if (id === 'motionArtwork') {
    return intl.formatMessage(kitMessages.motionLine);
  }
  if (id === 'musicVideo') {
    return view.state === 'made' ? null : intl.formatMessage(addedAssetsMessages.musicVideoLine);
  }
  return null;
}

/**
 * The lines of a ready version of a long video (design music-video a5, a6): a linked YouTube video
 * has no file; an uploaded music video can feed posts and gets its sound check (D4: a mismatch is
 * an amber warning, a match or an unknown verdict shows nothing); a made music video names its cut.
 */
function readyLines(intl: IntlShape, view: KitSlotView, title: string): KitSlotLine[] {
  const asset = view.readyAsset;
  if (view.state !== 'made' || asset === null) {
    return [];
  }
  const source = sourceOfAsset(asset);
  if (source === 'youtube') {
    return [{ text: intl.formatMessage(addedAssetsMessages.noFileLine), bad: false }];
  }
  if (view.slot.id !== 'musicVideo') {
    return [];
  }
  if (source === 'upload') {
    const lines: KitSlotLine[] = [{ text: intl.formatMessage(addedAssetsMessages.studioCanCut), bad: false }];
    if (isSoundMismatch(soundCheckOfAsset(asset))) {
      lines.unshift({ text: intl.formatMessage(addedAssetsMessages.soundMismatch, { title }), bad: false, warn: true });
    }
    return lines;
  }
  const cut = asset.params.musicVideoCutNumber;
  return cut === undefined
    ? []
    : [{ text: intl.formatMessage(addedAssetsMessages.madeCut, { number: cut }), bad: false }];
}

export interface KitSlotLine {
  text: string;
  bad: boolean;
  /** An amber warning (the sound check of an uploaded music video). */
  warn?: boolean;
}

/**
 * The lines under the name of a slot (design b1, d3): the fail sentence with the refund, "r2
 * failed. r1 stays.", "r2 is being made. r1 stays.", the Pro line of a locked slot, else the fixed
 * line of the type. Pure.
 */
export function kitSlotLines(
  intl: IntlShape,
  view: KitSlotView,
  context: { name: string; title: string; masterFailReason: CatalogMasterFailReason | null }
): KitSlotLine[] {
  const lines: KitSlotLine[] = [];
  const fixed = fixedLine(intl, view);
  if (fixed !== null) {
    lines.push({ text: fixed, bad: false });
  }
  lines.push(...readyLines(intl, view, context.title));
  const { failedAsset, readyAsset, workingAsset } = view;
  if (view.state === 'failed' && failedAsset !== null && view.slot.themeId !== null) {
    const reason = kitFailText(
      intl,
      { themeId: view.slot.themeId, failCode: failedAsset.failCode, credits: failedAsset.credits },
      context.masterFailReason
    );
    // A full video and the motion artwork say the refund in their own sentence (`kitFailText`).
    const back = failedAsset.credits > 0 && !usesRefundLine(view.slot.themeId, failedAsset.failCode);
    lines.push({ text: back ? `${reason} ${intl.formatMessage(kitMessages.creditsBack)}` : reason, bad: true });
  }
  if (view.state === 'made' && readyAsset !== null) {
    if (failedAsset !== null && failedAsset.version > readyAsset.version) {
      lines.push({
        text: intl.formatMessage(kitMessages.newerFailed, { failed: failedAsset.version, ready: readyAsset.version }),
        bad: true,
      });
    }
    if (workingAsset !== null && workingAsset.version > readyAsset.version) {
      lines.push({
        text: intl.formatMessage(kitMessages.newerMaking, { next: workingAsset.version, ready: readyAsset.version }),
        bad: false,
      });
    }
  }
  if (view.state === 'locked') {
    lines.push({
      text: intl.formatMessage(kitMessages.lockedLine, { name: context.name, title: context.title }),
      bad: false,
    });
  }
  return lines;
}
